import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TrendCard = ({ s, i }) => {
  const over = s.call === "OVER";
  return (
    <div data-testid={`ai-trend-stat-${i}`} className="rounded-xl border border-[#233248] bg-[#0B0F17]/60 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-bold text-white text-sm">{s.metric}</div>
          <div className="text-[11px] text-[#A78BFA] font-data">{s.pattern}</div>
        </div>
        <div className="text-right shrink-0">
          <span data-testid={`ai-trend-call-${i}`}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold font-data ${
              over ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
            {over ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {s.call} {s.line}
          </span>
          <div className="text-[10px] text-slate-500 font-data mt-0.5">{s.confidence}% confidence</div>
        </div>
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="font-data text-xl font-extrabold text-white">{s.expected}</span>
        <span className="text-[11px] text-slate-400">expected next game</span>
        <span className="ml-auto font-data text-[11px] text-slate-400">range {s.range_low}–{s.range_high}</span>
      </div>
      <p className="text-xs text-slate-300 leading-relaxed mt-2">{s.analysis}</p>
    </div>
  );
};

// AI reads the real game-by-game pattern for every stat in the selected timeframe and calls the next game.
export const AITrendPanel = ({ player, frame }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => setData(null), [player.id, frame.key]);

  const run = async () => {
    if (!user) {
      toast.error("Sign in to run the AI Trend Analysis");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/ai/player-trends", { player_id: player.id, frame: frame.key });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI trend analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="ai-trend-panel" className="rounded-xl border border-[#A78BFA]/30 bg-gradient-to-br from-[#A78BFA]/10 to-[#00E5FF]/5 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-8 w-8 rounded-lg bg-[#A78BFA]/20 text-[#A78BFA]"><Brain className="h-4 w-4" /></span>
          <div>
            <div className="font-heading font-bold text-white text-sm">AI Trend Analysis</div>
            <div className="text-[11px] text-slate-400">Reads the {frame.label} pattern on every stat and calls the next game</div>
          </div>
        </div>
        <button data-testid="ai-trend-run" onClick={run} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#A78BFA] text-[#0B0F17] text-xs font-bold px-3 py-2 hover:brightness-110 disabled:opacity-60 transition-[filter]">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
          {loading ? "Reading trends…" : data ? "Re-analyze" : "Analyze trends"}
        </button>
      </div>
      {data && (
        <div className="mt-3 space-y-2">
          <p data-testid="ai-trend-summary" className="text-sm text-slate-200 leading-relaxed">{data.summary}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {data.stats.map((s, i) => <TrendCard key={s.metric} s={s} i={i} />)}
          </div>
        </div>
      )}
    </div>
  );
};
